import { INSURANCE_COVERAGE_ORDER, ReferralInsurance } from '../../../interfaces/referral';

/** The subset of a document's flat extraction the insurance fallback reads. */
export interface ExtractedInsuranceFields {
  patient_primary_insurance_payer_name?: string;
  patient_primary_insurance_policy_id?: string;
  patient_primary_insurance_group_number?: string;
  patient_primary_insurance_policy_holder_name?: string;
  patient_secondary_insurance_payer_name?: string;
  patient_secondary_insurance_policy_id?: string;
  patient_secondary_insurance_group_number?: string;
  patient_secondary_insurance_policy_holder_name?: string;
}

const blankCoverage = (type: ReferralInsurance['type']): ReferralInsurance => ({
  type,
  carrier: '',
  policyNumber: '',
  groupNumber: '',
  policyHolder: '',
});

/**
 * The insurance rows the edit form starts from.
 *
 * The *load* half of the round trip whose save half is `buildReferralUpdateRequest`, and the
 * half where the data loss actually happened: the form picked four fields off each stored
 * coverage by hand, and since the save sends the whole array back, opening a referral and
 * pressing Save erased the payer id, plan type, subscriber and coverage window the record
 * held. Extracted so that a future re-narrowing of this literal fails a test instead of
 * silently losing data on the next save.
 *
 * Stored coverages win; the document's flat extraction is the fallback for a referral whose
 * insurance was never structured; and the form always has at least one row to edit.
 */
export const buildEditableInsurance = (
  stored: ReferralInsurance[] | null | undefined,
  extracted: ExtractedInsuranceFields,
): ReferralInsurance[] => {
  if (stored?.length) {
    // Spread first, then default the four editable fields — every other field survives.
    return stored.map((coverage) => ({
      ...coverage,
      type: coverage.type || 'primary',
      carrier: coverage.carrier || '',
      policyNumber: coverage.policyNumber || '',
      groupNumber: coverage.groupNumber || '',
      policyHolder: coverage.policyHolder || '',
    }));
  }

  const fromExtraction: ReferralInsurance[] = [];

  if (extracted.patient_primary_insurance_payer_name) {
    fromExtraction.push({
      type: 'primary',
      carrier: extracted.patient_primary_insurance_payer_name || '',
      policyNumber: extracted.patient_primary_insurance_policy_id || '',
      groupNumber: extracted.patient_primary_insurance_group_number || '',
      policyHolder: extracted.patient_primary_insurance_policy_holder_name || '',
    });
  }

  if (extracted.patient_secondary_insurance_payer_name) {
    fromExtraction.push({
      type: 'secondary',
      carrier: extracted.patient_secondary_insurance_payer_name || '',
      policyNumber: extracted.patient_secondary_insurance_policy_id || '',
      groupNumber: extracted.patient_secondary_insurance_group_number || '',
      policyHolder: extracted.patient_secondary_insurance_policy_holder_name || '',
    });
  }

  return fromExtraction.length ? fromExtraction : [blankCoverage('primary')];
};

/**
 * The coverage order a newly added row should take: the first one not already on the form.
 *
 * Not `INSURANCE_COVERAGE_ORDER[rows.length]`. That picked by count, so removing the primary
 * from a primary/secondary pair and adding a row produced two `secondary` rows and no primary
 * — a coverage the API would store and eligibility would never check.
 */
export const nextCoverageType = (
  rows: Pick<ReferralInsurance, 'type'>[],
): ReferralInsurance['type'] | null => {
  const taken = new Set(rows.map((row) => row.type));
  return INSURANCE_COVERAGE_ORDER.find((type) => !taken.has(type)) ?? null;
};
